import { Select, Tabs, Input, Button, List, Form } from 'antd'
import { FC, memo, useMemo, useState, useRef, useEffect } from 'react'
import styles from './index.module.scss'
import { useTranslation } from '@/services/i18n'
import CoinLogo from '../CoinLogo'
import PaginationList from '../PaginationList'
import { WalletItemModel } from '@/services/apis/wallet/models'
import { WalletCoin } from '@/services/apis/coin/models'
import WalletBadge from '@/components/WalletBadge'
import classNames from 'classnames'
import { DestinationTypeEnum } from '@/constants/enum'
import { useGetWhiteListAddressBook, useGetWhiteListAddressCount } from '@/hooks/policy'
import { useCoinBasis } from '@/hooks/coin'
import { AddressBookModel } from '@/services/apis/policy/model'
import { DEFAULT_PAGE_SIZE } from '@/constants'
import { useGetAssetWallets } from '@/hooks/wallet'
import { useParams } from 'react-router-dom'
import { CheckAddrDTO } from '@/services/apis/tx/models'
import { useWindowSize } from '@/hooks/screen'
import { formatUSD } from '@/utils/format'

const { Search, TextArea } = Input

const WalletItem: FC<
  WalletItemModel & { onClick: (data: string) => void; receiveWalletKey: string }
> = ({ accountName, usdBalance, accountType, accountKey, onClick, receiveWalletKey }) => {
  return (
    <List.Item
      className={classNames({
        [styles.listItem]: true,
        [styles.isSelected]: accountKey === receiveWalletKey,
      })}
      onClick={() => onClick(accountKey)}
    >
      <div className={styles.listName}>{accountName}</div>
      <div className={styles.walletBalance}>$ {formatUSD(usdBalance)}</div>
      <WalletBadge type={accountType} />
    </List.Item>
  )
}

interface CommonPanelProps {
  onClose: () => void
  onNext: (data: Omit<CheckAddrDTO, 'coinKey'>, silence?: boolean) => Promise<string | undefined>
  coinKey: string
  isChecking: boolean
}

const useListHeight = (list: HTMLDivElement, totalPages: number) => {
  const windowSize = useWindowSize()
  const [height, setHeight] = useState(0)
  useEffect(() => {
    const FOOTER_HEIGHT = totalPages > 1 ? 135 : 100
    const { y = 0 } = list?.getBoundingClientRect?.() ?? {}
    setHeight(windowSize[1] - y - FOOTER_HEIGHT)
  }, [windowSize[1], totalPages])
  return height
}

const WalletListPanel: FC<CommonPanelProps> = ({ onClose, onNext, isChecking }) => {
  const { walletKey } = useParams()
  const { t } = useTranslation()
  const [searchVal, setSearchVal] = useState('')
  const ref = useRef<any>(null)
  const { data, isLoading, setCurrentPage, currentPage } = useGetAssetWallets(searchVal, walletKey)
  const height = useListHeight(ref.current, data.totalPages)
  const [receiveWalletKey, setReceiveWalletKey] = useState('')
  const onClick = () => {
    onNext({
      accountKey: receiveWalletKey,
      destinationType: DestinationTypeEnum.VAULT_ACCOUNT,
      address: '',
    })
  }
  const onSearch = (val: string) => {
    setReceiveWalletKey('')
    setSearchVal(val)
  }
  return (
    <>
      <Search
        onSearch={onSearch}
        placeholder={t('wallet.sendDrawer.tab1.placeholder') || ''}
        enterButton={<Button type="primary">{t('common.search')}</Button>}
        className={styles.search}
      />
      <div style={{ height, overflow: 'auto' }} ref={ref}>
        <PaginationList
          data={data}
          loading={isLoading}
          current={currentPage}
          onChange={setCurrentPage}
          renderItem={item => (
            <WalletItem
              {...(item as WalletItemModel)}
              onClick={setReceiveWalletKey}
              receiveWalletKey={receiveWalletKey}
            />
          )}
        />
      </div>
      <div className={styles.footer}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button type="primary" onClick={onClick} loading={isChecking} disabled={!receiveWalletKey}>
          {t('common.next')}
        </Button>
      </div>
    </>
  )
}

interface AddressItemProps extends AddressBookModel {
  onClick: (data: string) => void
  receiveAddress: string
}

const AddressItem: FC<AddressItemProps> = ({
  blockchainTypeName,
  addressName,
  address,
  onClick,
  receiveAddress,
}) => {
  return (
    <List.Item
      className={classNames({
        [styles.listItem]: true,
        [styles.isSelected]: address === receiveAddress,
      })}
      onClick={() => onClick(address)}
    >
      <div className="flex items-center">
        <div className={styles.listName}>{addressName}</div>
        <span className={styles.blockChainLabel}>{blockchainTypeName}</span>
      </div>
      <p className={styles.address}>{address}</p>
    </List.Item>
  )
}

const WhitelistPanel: FC<CommonPanelProps> = ({ onClose, onNext, coinKey, isChecking }) => {
  const [searchVal, setSearchVal] = useState('')
  const [address, setAddress] = useState('')
  const { t } = useTranslation()
  const coinBasis = useCoinBasis(coinKey)
  const blockchainTypeKey = coinBasis?.blockchainTypeKey!
  const totalElements = useGetWhiteListAddressCount(blockchainTypeKey)
  const { data, loading, currentPage, setCurrentPage } = useGetWhiteListAddressBook(
    blockchainTypeKey,
    searchVal
  )
  const pagination = {
    totalElements,
    totalPages: totalElements / DEFAULT_PAGE_SIZE,
    content: data,
    pageNumber: currentPage,
  }
  const ref = useRef<any>()
  const height = useListHeight(ref.current, pagination.totalPages)
  const onClick = () => {
    onNext({
      accountKey: '',
      destinationType: DestinationTypeEnum.INTERNAL_WALLET,
      address,
    })
  }
  const onSearch = (val: string) => {
    setAddress('')
    setSearchVal(val)
  }
  return (
    <>
      <Search
        onSearch={onSearch}
        placeholder={t('wallet.sendDrawer.tab2.placeholder') || ''}
        enterButton={<Button type="primary">{t('common.search')}</Button>}
        className={styles.search}
      />
      <div style={{ height, overflow: 'auto' }} ref={ref}>
        <PaginationList
          data={pagination}
          renderItem={item => (
            <AddressItem
              {...(item as AddressBookModel)}
              onClick={setAddress}
              receiveAddress={address}
            />
          )}
          emptyText={t('wallet.sendDrawer.tab2.empty')!}
          loading={loading}
          current={currentPage}
          onChange={setCurrentPage}
        />
      </div>
      <div className={styles.footer}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button type="primary" onClick={onClick} loading={isChecking} disabled={!address}>
          {t('common.next')}
        </Button>
      </div>
    </>
  )
}

const InputAddressPanel: FC<CommonPanelProps> = ({ onClose, onNext, isChecking }) => {
  const { t } = useTranslation()
  const [errMsg, setErrMsg] = useState<string>()
  const [address, setAddress] = useState('')
  const onClick = async () => {
    const errMsg = await onNext(
      {
        accountKey: '',
        address,
        destinationType: DestinationTypeEnum.ONE_TIME_ADDRESS,
      },
      true
    )
    setErrMsg(errMsg)
  }
  return (
    <>
      <Form.Item
        validateStatus={errMsg && 'error'}
        help={errMsg}
        className={styles.inputAddressItem}
      >
        <TextArea
          rows={4}
          placeholder={t('wallet.sendDrawer.tab3.placeholder') || ''}
          value={address}
          onChange={evt => setAddress(evt.target.value)}
        />
      </Form.Item>
      <div className={styles.footer}>
        <Button onClick={onClose}>{t('common.cancel')}</Button>
        <Button type="primary" onClick={onClick} loading={isChecking} disabled={!address}>
          {t('common.next')}
        </Button>
      </div>
    </>
  )
}

const SendDrawerTabs: FC<CommonPanelProps> = props => {
  const { t } = useTranslation()
  const items = [
    {
      label: t('wallet.sendDrawer.tab1.name'),
      key: 'assetWallet',
      children: <WalletListPanel {...props} />,
    },
    {
      label: t('wallet.sendDrawer.tab2.name'),
      key: 'whitelist',
      children: <WhitelistPanel {...props} />,
    },
    {
      label: t('wallet.sendDrawer.tab3.name'),
      key: 'inputAddress',
      children: <InputAddressPanel {...props} />,
    },
  ]
  return <Tabs defaultActiveKey="assetWallet" items={items} />
}

interface SelectReceiverProps {
  onClose: () => void
  onNext: (data: Omit<CheckAddrDTO, 'coinKey'>, silence?: boolean) => Promise<string | undefined>
  coins: WalletCoin[]
  onSelectCoin: (val: string) => void
  coinKey: string
  isChecking: boolean
}

const SelectReceiver: FC<SelectReceiverProps> = ({
  isChecking,
  onClose,
  onNext,
  coins,
  coinKey,
  onSelectCoin,
}) => {
  const { t } = useTranslation()
  const coinOptions = useMemo(() => {
    return coins
      .filter(coin => Number(coin.balance) > 0 || coin.coinKey === coinKey)
      .sort((a, b) => Number(b.balance) - Number(a.balance))
      .map(coin => ({
        label: (
          <div className={styles.option}>
            <div className={styles.optionLeft}>
              <CoinLogo logo={coin.logoUrl} networkLogo={coin.networkLogo} />
              <div className={styles.detail}>
                <div>
                  <span className={styles.coinName} style={{ paddingLeft: '0px' }}>
                    {coin.coinName}
                  </span>
                  <span className={styles.coinFullName}>({coin.coinFullName})</span>
                </div>
                <div style={{ marginTop: '4px' }}>{coin.networkName}</div>
              </div>
            </div>
            <div className={styles.optionRight}>
              <span className={styles.coinNum}>
                {formatUSD(coin.balance, coin.showCoinDecimal)}
              </span>
              <span>{`≈$ ${formatUSD(coin.usdBalance)}`}</span>
            </div>
          </div>
        ),
        value: coin.coinKey,
        key: coin.coinKey,
      }))
  }, [coins])
  return (
    <Form layout="vertical">
      <Form.Item label={t('wallet.sendDrawer.sendCoin')}>
        <Select
          onChange={onSelectCoin}
          options={coinOptions}
          value={coinKey}
          className={styles.select}
          popupClassName={styles.popupSelect}
          size="large"
        />
      </Form.Item>
      <SendDrawerTabs onClose={onClose} onNext={onNext} coinKey={coinKey} isChecking={isChecking} />
    </Form>
  )
}

export default memo(SelectReceiver)
